"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";

interface AccordionItem {
  question: string;
  answer: string;
  learnMoreHref?: string;
  learnMoreText?: string;
}

interface LearningAccordionProps {
  items: AccordionItem[];
  title?: string;
}

export function LearningAccordion({
  items,
  title = "Learning Resources",
}: LearningAccordionProps) {
  const [openId, setOpenId] = useState<number | null>(0); // First item open by default on mobile

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

      {items.map((item, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow"
        >
          <button
            onClick={() => setOpenId(openId === index ? null : index)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <span className="font-medium text-gray-900">{item.question}</span>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                openId === index ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Answer (expanded) */}
          {openId === index && (
            <div className="px-4 pb-4 pt-0 border-t border-gray-100">
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                {item.answer}
              </p>
              {item.learnMoreHref && (
                <Link href={item.learnMoreHref}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-purple-600 hover:text-purple-700 p-0 h-auto"
                  >
                    {item.learnMoreText || "Learn more →"}
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
